import type {
  CatalogProduct,
  OrderDetail,
  OrderItemOptionChoice,
  OutletKind,
} from '@horevia/contracts';
import { outletPaymentMethods } from '@horevia/contracts';
import {
  Badge,
  Button,
  CategoryTab,
  CategoryTabs,
  Container,
  EmptyState,
  ErrorState,
  Input,
  NewOrderChip,
  NoPermissionState,
  OrderChip,
  OrderChips,
  OrderStatusBadge,
  PosBar,
  PosBarButton,
  PosBarSpacer,
  PosBarStatus,
  PosBarTitle,
  PosMain,
  PosScreen,
  ProductCard,
  ProductGrid,
  Skeleton,
} from '@horevia/ui';
import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import {
  LayoutGrid,
  LogOut,
  Maximize2,
  Minimize2,
  ReceiptText,
  Search,
  Sparkles,
  Utensils,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSession } from '@/features/auth/session';
import { cashOverviewQueryOptions } from '@/features/payments/queries';
import { OrderContextSheet, type OrderContext } from '@/features/pos/OrderContextSheet';
import { OrderTicket } from '@/features/pos/OrderTicket';
import { PaymentStep } from '@/features/pos/PaymentStep';
import { ProductOptionsDialog } from '@/features/pos/ProductOptionsDialog';
import { happyHourDiscountLabel, happyHourFor } from '@/features/pos/happy-hour';
import { useOrderQueue } from '@/features/pos/order-queue';
import { useCurrentOutlet } from '@/features/pos/outlet';
import {
  addOrderItem,
  areasQueryOptions,
  categoriesQueryOptions,
  createOrder,
  currentHappyHoursQueryOptions,
  openOrdersQueryOptions,
  orderQueryOptions,
  productsQueryOptions,
  tablesQueryOptions,
  updateOrder,
} from '@/features/pos/queries';
import { categoryIcon, displayTone } from '@/features/pos/status';
import { errorMessage, errorTraceId } from '@/lib/errors';
import { formatAmount, formatTime } from '@/lib/format';
import { useCurrentProperty } from '@/lib/property';

interface PosSearch {
  /** Commande sélectionnée (reprise depuis le plan de salle ou l'historique). */
  order?: string;
  /** Table de la prochaine commande (plan de salle, table libre). */
  table?: string;
}

export const Route = createFileRoute('/_app/pos/')({
  validateSearch: (search: Record<string, unknown>): PosSearch => ({
    ...(typeof search['order'] === 'string' ? { order: search['order'] } : {}),
    ...(typeof search['table'] === 'string' ? { table: search['table'] } : {}),
  }),
  component: PosPage,
});

/** Contexte par défaut de la prochaine commande : comptoir pour un bar (ADR-019), sur place sinon. */
function defaultContext(kind: OutletKind | undefined, tableId?: string | null): OrderContext {
  if (tableId) return { serviceType: 'DINE_IN', tableId, covers: null, tabName: null };
  return {
    serviceType: kind === 'BAR' ? 'COUNTER' : 'DINE_IN',
    tableId: null,
    covers: null,
    tabName: null,
  };
}

/** Ligne à sélectionner après l'ajout d'un produit : la ligne non envoyée qui l'a reçu. */
function lineForProduct(
  order: OrderDetail,
  productId: string,
  options: OrderItemOptionChoice[],
  notes: string | null,
): string | null {
  const candidates = order.items.filter(
    (item) =>
      item.status === 'PENDING' &&
      item.productId === productId &&
      (options.length > 0 || (item.options.length === 0 && !item.notes)) &&
      (notes === null || item.notes === notes),
  );
  return candidates.sort((a, b) => b.position - a.position)[0]?.id ?? null;
}

/** Heure courante de la barre de caisse, rafraîchie toutes les 30 secondes. */
function useClock(locale: string): string {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 30_000);
    return () => window.clearInterval(timer);
  }, []);
  return formatTime(now.toISOString(), locale);
}

/** Plein écran du navigateur (facultatif, à la demande de l'utilisateur). */
function useFullscreen(): { supported: boolean; active: boolean; toggle: () => void } {
  const supported = typeof document !== 'undefined' && Boolean(document.fullscreenEnabled);
  const [active, setActive] = useState(() => Boolean(document.fullscreenElement));
  useEffect(() => {
    const handler = () => setActive(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);
  const toggle = useCallback(() => {
    if (document.fullscreenElement) void document.exitFullscreen();
    else void document.documentElement.requestFullscreen?.();
  }, []);
  return { supported, active, toggle };
}

/**
 * Caisse (charte §9, ADR-026) en mode plein écran : un tap sur un plat l'ajoute
 * sans aucune interruption (la commande se crée au premier plat), le
 * récapitulatif compte une ligne par article, la table et le mode de service
 * s'attribuent après la saisie, « Paiement » ouvre l'étape d'encaissement.
 */
function PosPage() {
  const { t, i18n } = useTranslation(['pos', 'common', 'errors']);
  const session = useSession();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const { property } = useCurrentProperty();
  const { outlets, outlet, outletId, setOutletId, isPending: outletsPending } = useCurrentOutlet();
  const canCreate = session.can('order.create');
  const canReadCash = session.can('cash.read');
  const canReadTables = session.can('table.read');
  const hasRestaurant = session.hasModule('restaurant');
  const hasBar = session.hasModule('bar');
  const clock = useClock(i18n.language);
  const fullscreen = useFullscreen();

  const [selectedOrderId, setSelectedOrderId] = useState<string | undefined>(search.order);
  const [draft, setDraft] = useState<OrderContext>(() =>
    defaultContext(outlet?.kind, search.table ?? null),
  );
  const [step, setStep] = useState<'order' | 'payment'>('order');
  const [sheet, setSheet] = useState<'assign' | 'browse' | null>(null);
  const [selectedLineId, setSelectedLineId] = useState<string | null>(null);
  const [view, setView] = useState<'catalog' | 'ticket'>('catalog');
  const [categoryId, setCategoryId] = useState<string>('ALL');
  const [term, setTerm] = useState('');
  const [optionsProduct, setOptionsProduct] = useState<CatalogProduct | null>(null);

  // Reprise d'une commande ou d'une table depuis le plan de salle / l'historique.
  useEffect(() => {
    if (search.order) {
      setSelectedOrderId(search.order);
      setStep('order');
    }
  }, [search.order]);
  useEffect(() => {
    if (search.table && !search.order) {
      setSelectedOrderId(undefined);
      setDraft(defaultContext(outlet?.kind, search.table));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- seule la table demandée compte
  }, [search.table]);
  // Changement de point de vente : contexte par défaut du nouveau point de vente.
  useEffect(() => {
    if (!selectedOrderId)
      setDraft((current) => (current.tableId ? current : defaultContext(outlet?.kind)));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- uniquement au changement de point de vente
  }, [outlet?.id, outlet?.kind]);

  const openOrders = useQuery(openOrdersQueryOptions(outletId ?? '', Boolean(outletId)));
  const categories = useQuery(
    categoriesQueryOptions({ propertyId: property?.id, outletId }, Boolean(outletId)),
  );
  const products = useQuery(
    productsQueryOptions({ propertyId: property?.id, outletId }, Boolean(outletId)),
  );
  const tablesEnabled = Boolean(outletId) && hasRestaurant && canReadTables;
  const tables = useQuery(tablesQueryOptions(outletId ?? '', tablesEnabled));
  const areas = useQuery(areasQueryOptions(outletId ?? '', tablesEnabled));
  const cash = useQuery(cashOverviewQueryOptions(canReadCash && Boolean(property), property?.id));
  const order = useQuery(orderQueryOptions(selectedOrderId, Boolean(selectedOrderId)));
  // Happy hours actives (module Bar, ADR-019) : bandeau et prix réduits, à titre indicatif.
  const happyHours = useQuery(
    currentHappyHoursQueryOptions(outletId ?? '', Boolean(outletId) && hasBar),
  );
  const activeRules = useMemo(
    () => (hasBar ? (happyHours.data?.rules ?? []) : []),
    [hasBar, happyHours.data],
  );

  const clearSearch = useCallback(() => {
    if (search.order || search.table) void navigate({ search: {}, replace: true });
  }, [navigate, search.order, search.table]);

  const queue = useOrderQueue({
    orderId: selectedOrderId,
    createOrder: () =>
      createOrder({
        outletId: outletId ?? '',
        serviceType: draft.serviceType,
        tableId: draft.serviceType === 'DINE_IN' ? draft.tableId : null,
        tabName: draft.tabName,
        covers: draft.covers,
      }),
    onCreated: (created, current) => {
      if (!current) return;
      setSelectedOrderId(created.id);
      clearSearch();
    },
  });

  /** « Nouvelle commande » : une commande encore vide est réutilisée. */
  const startNewOrder = useCallback(() => {
    setStep('order');
    setSelectedLineId(null);
    setView('catalog');
    const current = order.data;
    if (
      current &&
      current.status === 'OPEN' &&
      current.items.length === 0 &&
      current.paidTotal === 0
    ) {
      return;
    }
    setSelectedOrderId(undefined);
    queue.startDraft();
    setDraft(defaultContext(outlet?.kind));
    clearSearch();
  }, [order.data, queue, outlet?.kind, clearSearch]);

  const selectOrder = useCallback(
    (id: string) => {
      setSelectedOrderId(id);
      setStep('order');
      setSelectedLineId(null);
      clearSearch();
    },
    [clearSearch],
  );

  // Une commande close (payée, annulée, imputée) quitte la caisse — sauf reprise
  // explicite depuis l'historique (`?order=`) et pendant l'écran de fin de paiement.
  useEffect(() => {
    if (order.data && order.data.status !== 'OPEN' && !search.order && step === 'order') {
      setSelectedOrderId(undefined);
      queue.startDraft();
      setDraft(defaultContext(outlet?.kind));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- réaction à l'état de la commande
  }, [order.data?.status, search.order, step]);

  const visibleProducts = useMemo(() => {
    const list = products.data ?? [];
    const filtered = categoryId === 'ALL' ? list : list.filter((p) => p.categoryId === categoryId);
    const needle = term.trim().toLowerCase();
    return needle
      ? filtered.filter(
          (p) => p.name.toLowerCase().includes(needle) || p.code.toLowerCase().includes(needle),
        )
      : filtered;
  }, [products.data, categoryId, term]);

  const categoryById = useMemo(
    () => new Map((categories.data ?? []).map((category) => [category.id, category])),
    [categories.data],
  );

  /** Quantité déjà dans la commande (lignes non annulées) + taps en attente. */
  const orderedQuantity = useMemo(() => {
    const totals = new Map<string, number>();
    for (const item of order.data?.items ?? []) {
      if (!item.productId || item.status === 'CANCELLED') continue;
      totals.set(item.productId, (totals.get(item.productId) ?? 0) + item.quantity);
    }
    for (const [productId, count] of Object.entries(queue.queuedByProduct)) {
      totals.set(productId, (totals.get(productId) ?? 0) + count);
    }
    return totals;
  }, [order.data, queue.queuedByProduct]);

  const addProduct = (
    productId: string,
    quantity = 1,
    options: OrderItemOptionChoice[] = [],
    notes: string | null = null,
  ) => {
    void queue
      .run(
        (current) =>
          addOrderItem(current.id, {
            productId,
            quantity,
            options,
            notes,
            version: current.version,
          }),
        { productId },
      )
      .then((updated) => {
        if (updated) setSelectedLineId(lineForProduct(updated, productId, options, notes));
      });
  };

  const selectProduct = (product: CatalogProduct) => {
    if (!product.isAvailable) return;
    if (product.optionGroups.length > 0) setOptionsProduct(product);
    else addProduct(product.id);
  };

  const applyContext = (next: OrderContext) => {
    const tableId = next.serviceType === 'DINE_IN' ? next.tableId : null;
    if (!selectedOrderId) {
      setDraft({ ...next, tableId });
      setSheet(null);
      return;
    }
    void queue
      .run((current) =>
        updateOrder(current.id, {
          serviceType: next.serviceType,
          tableId,
          covers: next.covers,
          tabName: next.tabName,
          version: current.version,
        }),
      )
      .then((updated) => {
        if (updated) setSheet(null);
      });
  };

  if (!canCreate) {
    return (
      <Container>
        <NoPermissionState
          title={t('common:states.noPermissionTitle')}
          description={t('common:states.noPermissionDescription')}
        />
        <ExitLink />
      </Container>
    );
  }

  if (outletsPending) {
    return (
      <Container>
        <div className="stack" aria-busy="true">
          <Skeleton height={56} />
          <Skeleton height={320} />
        </div>
      </Container>
    );
  }

  if (!outlet) {
    return (
      <Container>
        <EmptyState
          icon={<Utensils className="ico" aria-hidden="true" />}
          title={t('pos:noOutlet.title')}
          description={t('pos:noOutlet.body')}
          action={
            session.can('outlet.manage') ? (
              <Button variant="primary" asChild>
                <Link to="/pos/settings">{t('pos:noOutlet.action')}</Link>
              </Button>
            ) : undefined
          }
        />
        <ExitLink />
      </Container>
    );
  }

  const cashOpen = cash.data?.currentSession?.status === 'OPEN';
  const orders = openOrders.data ?? [];
  const current = order.data ?? null;
  const methods = outletPaymentMethods(outlet.settings);
  const tableList = tables.data ?? [];
  const draftTableCode = draft.tableId
    ? (tableList.find((table) => table.id === draft.tableId)?.code ?? null)
    : null;
  const context: OrderContext = current
    ? {
        serviceType: current.serviceType,
        tableId: current.tableId,
        covers: current.covers,
        tabName: current.tabName,
      }
    : draft;
  const itemsCount = (current?.items ?? [])
    .filter((item) => item.status !== 'CANCELLED')
    .reduce((sum, item) => sum + item.quantity, 0);

  return (
    <PosScreen view={view}>
      <PosBar>
        <PosBarTitle>
          <ReceiptText className="ico" aria-hidden="true" />
          {t('pos:title')}
        </PosBarTitle>
        {outlets.length > 1 ? (
          <select
            aria-label={t('pos:bar.outlet')}
            value={outlet.id}
            onChange={(event) => {
              setOutletId(event.target.value);
              setSelectedOrderId(undefined);
              setStep('order');
            }}
          >
            {outlets.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </select>
        ) : (
          <span className="pos__bar-status">{outlet.name}</span>
        )}
        {canReadCash ? (
          <PosBarStatus on={cashOpen}>
            {cashOpen ? t('pos:bar.cashOpen') : t('pos:bar.cashClosed')}
          </PosBarStatus>
        ) : null}
        <PosBarSpacer />
        <span className="pos__bar-status pos__bar-optional">{session.user.fullName}</span>
        <span
          className="pos__bar-status pos__bar-clock pos__bar-optional"
          aria-label={t('pos:bar.clock')}
        >
          {clock}
        </span>
        {tablesEnabled && tableList.length > 0 ? (
          <PosBarButton
            icon={<LayoutGrid className="ico" aria-hidden="true" />}
            onClick={() => setSheet('browse')}
          >
            {t('pos:bar.tables')}
          </PosBarButton>
        ) : null}
        {fullscreen.supported ? (
          <PosBarButton
            className="pos__bar-optional"
            icon={
              fullscreen.active ? (
                <Minimize2 className="ico" aria-hidden="true" />
              ) : (
                <Maximize2 className="ico" aria-hidden="true" />
              )
            }
            aria-pressed={fullscreen.active}
            onClick={fullscreen.toggle}
          >
            {fullscreen.active ? t('pos:bar.exitFullscreen') : t('pos:bar.fullscreen')}
          </PosBarButton>
        ) : null}
        <PosBarButton
          emphasis
          icon={<LogOut className="ico" aria-hidden="true" />}
          onClick={() => {
            if (document.fullscreenElement) void document.exitFullscreen();
            void navigate({ to: '/' });
          }}
        >
          {t('pos:bar.exit')}
        </PosBarButton>
      </PosBar>

      {step === 'payment' && current ? (
        <PaymentStep
          key={current.id}
          order={current}
          methods={methods}
          queue={queue}
          onBack={() => setStep('order')}
          onNewOrder={() => {
            setStep('order');
            setSelectedOrderId(undefined);
            queue.startDraft();
            setDraft(defaultContext(outlet.kind));
            setSelectedLineId(null);
            setView('catalog');
            clearSearch();
          }}
        />
      ) : (
        <>
          <PosMain>
            {activeRules.length > 0 ? (
              <div className="happy-banner" role="status">
                <span className="happy-banner__title">
                  <Sparkles className="ico ico-16" aria-hidden="true" />
                  {t('pos:bar.happyHour')}
                </span>
                {activeRules.map((rule) => (
                  <span key={rule.id} className="happy-banner__rule">
                    <strong>{rule.name}</strong>
                    <span>{happyHourDiscountLabel(rule, session.user.tenant.currencyCode)}</span>
                    <span className="muted">
                      ·{' '}
                      {rule.categoryIds.length === 0
                        ? t('pos:bar.happyHourAll')
                        : rule.categoryIds
                            .map((id) => categoryById.get(id)?.name)
                            .filter(Boolean)
                            .join(', ')}{' '}
                      · {t('pos:bar.happyHourUntil', { time: rule.endTime })}
                    </span>
                  </span>
                ))}
              </div>
            ) : null}

            <OrderChips aria-label={t('pos:bar.openOrders', { count: orders.length })}>
              <NewOrderChip onClick={startNewOrder}>{t('pos:bar.newOrder')}</NewOrderChip>
              {orders.map((item) => (
                <OrderChip
                  key={item.id}
                  active={item.id === selectedOrderId}
                  number={`#${item.dailyNumber}`}
                  where={
                    item.tableCode
                      ? `${t('pos:newOrder.table')} ${item.tableCode}`
                      : item.tabName
                        ? t('pos:ticket.tab', { name: item.tabName })
                        : t(`pos:serviceType.${item.serviceType}`)
                  }
                  status={
                    <OrderStatusBadge tone={displayTone(item.status, item.preparationStatus)}>
                      {t(`pos:preparation.${item.preparationStatus}`)}
                    </OrderStatusBadge>
                  }
                  amount={formatAmount(item.total, i18n.language, item.currencyCode)}
                  onClick={() => selectOrder(item.id)}
                />
              ))}
            </OrderChips>

            {categories.isError || products.isError ? (
              <ErrorState
                title={t('common:states.errorTitle')}
                description={errorMessage(categories.error ?? products.error, t)}
                traceId={errorTraceId(categories.error ?? products.error)}
                retryLabel={t('common:actions.retry')}
                onRetry={() => {
                  void categories.refetch();
                  void products.refetch();
                }}
                traceCopyLabel={t('common:actions.copy')}
                traceCopiedLabel={t('common:actions.copied')}
              />
            ) : (
              <>
                <CategoryTabs aria-label={t('pos:catalog.title')}>
                  <CategoryTab
                    active={categoryId === 'ALL'}
                    icon={<LayoutGrid className="ico ico-16" aria-hidden="true" />}
                    color="SLATE"
                    count={products.data?.length}
                    onClick={() => setCategoryId('ALL')}
                  >
                    {t('pos:categories.all')}
                  </CategoryTab>
                  {(categories.data ?? []).map((category) => {
                    const Icon = categoryIcon(category.icon);
                    return (
                      <CategoryTab
                        key={category.id}
                        active={categoryId === category.id}
                        icon={<Icon className="ico ico-16" aria-hidden="true" />}
                        color={category.color}
                        count={category.productsCount}
                        onClick={() => setCategoryId(category.id)}
                      >
                        {category.name}
                      </CategoryTab>
                    );
                  })}
                </CategoryTabs>

                <div className="pos__tools">
                  <Input
                    aria-label={t('pos:categories.search')}
                    placeholder={t('pos:categories.search')}
                    icon={<Search className="ico" aria-hidden="true" />}
                    value={term}
                    onChange={(event) => setTerm(event.target.value)}
                  />
                  {term ? (
                    <Button variant="ghost" onClick={() => setTerm('')}>
                      {t('pos:categories.clear')}
                    </Button>
                  ) : null}
                </div>

                <div className="pos__scroll">
                  {products.isPending ? (
                    <Skeleton height={240} />
                  ) : visibleProducts.length === 0 ? (
                    <EmptyState
                      title={t('pos:catalog.empty')}
                      description={t('pos:categories.empty')}
                    />
                  ) : (
                    <ProductGrid>
                      {visibleProducts.map((product) => {
                        const category = categoryById.get(product.categoryId);
                        const Icon = categoryIcon(category?.icon ?? 'utensils');
                        const quantity = orderedQuantity.get(product.id) ?? 0;
                        const deal = happyHourFor(product, activeRules);
                        return (
                          <ProductCard
                            key={product.id}
                            className={deal ? 'product-card--happy' : undefined}
                            name={product.name}
                            price={
                              deal ? (
                                <>
                                  <span className="price-strike">
                                    {formatAmount(
                                      product.price,
                                      i18n.language,
                                      product.currencyCode,
                                    )}
                                  </span>
                                  {formatAmount(deal.price, i18n.language, product.currencyCode)}
                                </>
                              ) : (
                                formatAmount(product.price, i18n.language, product.currencyCode)
                              )
                            }
                            icon={<Icon className="ico" aria-hidden="true" />}
                            color={category?.color ?? 'GREEN'}
                            quantity={quantity}
                            selected={quantity > 0}
                            unavailable={!product.isAvailable}
                            aria-label={t('pos:product.add', { name: product.name })}
                            onClick={() => selectProduct(product)}
                            badges={
                              !product.isAvailable || product.optionGroups.length > 0 || deal ? (
                                <>
                                  {!product.isAvailable ? (
                                    <Badge tone="red">{t('pos:product.unavailable')}</Badge>
                                  ) : null}
                                  {product.optionGroups.length > 0 ? (
                                    <Badge tone="neutral">{t('pos:product.options')}</Badge>
                                  ) : null}
                                  {deal ? (
                                    <Badge tone="green">{t('pos:product.happyHour')}</Badge>
                                  ) : null}
                                </>
                              ) : undefined
                            }
                          />
                        );
                      })}
                    </ProductGrid>
                  )}
                </div>
              </>
            )}
          </PosMain>

          <OrderTicket
            key={current?.id ?? 'next'}
            order={current}
            draft={draft}
            draftTableCode={draftTableCode}
            currencyCode={session.user.tenant.currencyCode}
            queue={queue}
            selectedLineId={selectedLineId}
            onSelectLine={setSelectedLineId}
            onAssign={() => setSheet('assign')}
            onPay={() => setStep('payment')}
            onClosed={startNewOrder}
          />

          <div className="pos__switch" role="group" aria-label={t('pos:bar.views')}>
            <Button
              variant={view === 'catalog' ? 'primary' : 'secondary'}
              size="lg"
              aria-pressed={view === 'catalog'}
              onClick={() => setView('catalog')}
            >
              {t('pos:bar.viewCatalog')}
            </Button>
            <Button
              variant={view === 'ticket' ? 'primary' : 'secondary'}
              size="lg"
              aria-pressed={view === 'ticket'}
              onClick={() => setView('ticket')}
            >
              {t('pos:bar.viewTicket', {
                count: itemsCount,
                total: formatAmount(
                  current?.total ?? 0,
                  i18n.language,
                  current?.currencyCode ?? session.user.tenant.currencyCode,
                ),
              })}
            </Button>
          </div>
        </>
      )}

      <OrderContextSheet
        open={sheet !== null}
        onOpenChange={(open) => !open && setSheet(null)}
        mode={sheet ?? 'assign'}
        outletKind={outlet.kind}
        tables={tableList}
        areas={areas.data ?? []}
        openOrders={orders}
        value={context}
        currentOrderId={current?.id}
        pending={queue.pending > 0}
        onApply={applyContext}
        onOpenOrder={(id) => {
          selectOrder(id);
          setSheet(null);
        }}
        onStartOnTable={(tableId) => {
          setSelectedOrderId(undefined);
          queue.startDraft();
          setDraft(defaultContext(outlet.kind, tableId));
          setStep('order');
          setSelectedLineId(null);
          setSheet(null);
          clearSearch();
        }}
      />
      <ProductOptionsDialog
        product={optionsProduct}
        open={optionsProduct !== null}
        onOpenChange={(open) => !open && setOptionsProduct(null)}
        onAdd={(input) => {
          if (!optionsProduct) return;
          addProduct(optionsProduct.id, input.quantity, input.options, input.notes);
          setOptionsProduct(null);
        }}
      />
    </PosScreen>
  );
}

/** Sortie du mode caisse quand la caisse ne peut pas s'afficher (droits, point de vente). */
function ExitLink() {
  const { t } = useTranslation(['pos']);
  return (
    <div className="row" style={{ justifyContent: 'center', marginTop: 16 }}>
      <Button variant="secondary" size="lg" asChild>
        <Link to="/">
          <LogOut className="ico" aria-hidden="true" />
          {t('pos:bar.exit')}
        </Link>
      </Button>
    </div>
  );
}
